import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Select, SelectProps, Spin } from "antd";
import { debounce } from "lodash";

export interface DebounceSelectProps<ValueType = any> extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  onScrollToBottom?: (search: string, pageNumber: number) => Promise<ValueType[]>;
  debounceTimeout?: number;
  selected?: any;
}

function DebounceSelect<ValueType extends { key?: string; label: React.ReactNode; value: string | number } = any>({
  fetchOptions,
  onScrollToBottom,
  debounceTimeout = 500,
  selected,
  onInputKeyDown,
  ...props
}: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);

  // Id of the most recent request; responses for older requests are dropped.
  const fetchRef = useRef(0);
  const searchValueRef = useRef("");
  // True from the moment the user types until the results for that text have arrived.
  const searchPendingRef = useRef(false);
  const pageNumberRef = useRef(1);
  const lastPageReachedRef = useRef(false);

  // Callers typically pass inline callbacks, so keep the latest ones in refs to avoid
  // re-creating the debounced search (and losing pending calls) on every render.
  const fetchOptionsRef = useRef(fetchOptions);
  fetchOptionsRef.current = fetchOptions;
  const onScrollToBottomRef = useRef(onScrollToBottom);
  onScrollToBottomRef.current = onScrollToBottom;

  const resetPagination = () => {
    pageNumberRef.current = 1;
    lastPageReachedRef.current = false;
  };

  const runSearch = useCallback((value: string) => {
    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    setFetching(true);

    fetchOptionsRef.current(value)
      .then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return; // superseded by a newer search
        }
        searchPendingRef.current = false;
        setOptions(newOptions ?? []);
        setFetching(false);
      })
      .catch(() => {
        if (fetchId !== fetchRef.current) {
          return;
        }
        searchPendingRef.current = false;
        setOptions([]);
        setFetching(false);
      });
  }, []);

  const debouncedSearch = useMemo(() => debounce(runSearch, debounceTimeout), [runSearch, debounceTimeout]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (value: string) => {
    // Invalidate any in-flight request and drop the previous results right away, so that pressing
    // Enter before the debounce fires cannot select an option from the previous search (FOC-5606).
    fetchRef.current += 1;
    searchValueRef.current = value;
    searchPendingRef.current = true;
    resetPagination();
    setOptions([]);
    setFetching(true);
    debouncedSearch(value);
  };

  const handleInputKeyDown: SelectProps["onInputKeyDown"] = (event) => {
    if (event.key === "Enter" && searchPendingRef.current) {
      // Run the pending search now instead of waiting out the debounce, and keep the dropdown open
      // for its results: without this, rc-select treats Enter as "pick the highlighted option"
      // (there is none yet) and closes the dropdown, so the results would load out of sight.
      debouncedSearch.flush();
      event.preventDefault();
      event.stopPropagation();
    }
    onInputKeyDown?.(event);
  };

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const scrolledToBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 1;

    // Without a paging callback there is nothing more to load, so never enter the loading
    // state here: it would otherwise spin forever (FOC-5605).
    if (!onScrollToBottomRef.current || lastPageReachedRef.current || fetching || !scrolledToBottom) {
      return;
    }

    setFetching(true);
    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    pageNumberRef.current += 1;

    onScrollToBottomRef.current(searchValueRef.current, pageNumberRef.current)
      .then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }
        setFetching(false);
        if (!newOptions?.length) {
          lastPageReachedRef.current = true;
          return;
        }
        setOptions((current) => [...current, ...newOptions]);
      })
      .catch(() => {
        if (fetchId === fetchRef.current) {
          setFetching(false);
        }
      });
  };

  // Callers that manage the selection pass `selected` (null = nothing selected, shows the placeholder).
  return (
    <Select
      showSearch
      allowClear
      labelInValue
      {...(selected !== undefined ? { value: selected } : {})}
      filterOption={false}
      size="large"
      onSearch={handleSearch}
      onInputKeyDown={handleInputKeyDown}
      onPopupScroll={handlePopupScroll}
      loading={fetching}
      onBlur={resetPagination}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
}

export default DebounceSelect;
